import {
  DIGIT_FOR_LETTER,
  ISO_COUNTRIES,
  LAYOUTS,
  LETTERS_FOR_DIGIT,
  computeCheckDigit
} from "./chunk-YATZMKUB.js";

// src/generate/index.ts
function createRng(seed) {
  let a = seed >>> 0;
  return () => {
    a = a + 1831565813 >>> 0;
    let t = a;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
var SYLLABLES = [
  "KA",
  "RO",
  "MI",
  "LEN",
  "TOR",
  "VA",
  "SI",
  "NOR",
  "BEL",
  "DRA",
  "FEN",
  "GU",
  "HAL",
  "JO",
  "KES",
  "LU",
  "MAR",
  "NE",
  "OS",
  "PRA",
  "QUI",
  "RAN",
  "SAL",
  "TEM",
  "UL",
  "VEN",
  "WIL",
  "XO",
  "YA",
  "ZEN"
];
var COUNTRY_CODES = Object.keys(ISO_COUNTRIES);
var ALNUM = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
var pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];
var int = (rng, min, max) => min + Math.floor(rng() * (max - min + 1));
function inventName(rng) {
  let s = "";
  for (let i = int(rng, 2, 4); i > 0; i--) s += pick(rng, SYLLABLES);
  return s;
}
function randomDate(rng, fromYear, toYear) {
  const y = int(rng, fromYear, toYear);
  const m = int(rng, 1, 12);
  const d = int(rng, 1, new Date(Date.UTC(y, m, 0)).getUTCDate());
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}
function randomString(rng, length, alphabet = ALNUM) {
  let s = "";
  for (let i = 0; i < length; i++) s += pick(rng, [...alphabet]);
  return s;
}
var DOC_CODES = {
  TD3: ["P<", "PO", "PD", "PS"],
  TD1: ["I<", "ID", "AC", "IR"],
  TD2: ["I<", "ID", "AC"],
  MRVA: ["V<", "VS", "VT"],
  MRVB: ["V<", "VS", "VT"]
};
function randomDocument(options = {}) {
  const rng = options.rng ?? createRng(options.seed ?? 1);
  const format = options.format ?? pick(rng, ["TD1", "TD2", "TD3", "MRVA", "MRVB"]);
  const year = options.referenceYear ?? 2026;
  const state = pick(rng, COUNTRY_CODES);
  const given = Array.from({ length: int(rng, 1, 3) }, () => inventName(rng)).join(" ");
  const doc = {
    documentCode: pick(rng, DOC_CODES[format]),
    issuingState: state,
    surname: rng() < 0.15 ? `${inventName(rng)} ${inventName(rng)}` : inventName(rng),
    givenNames: rng() < 0.05 ? "" : given,
    documentNumber: randomString(rng, 9),
    nationality: rng() < 0.85 ? state : pick(rng, COUNTRY_CODES),
    birthDate: randomDate(rng, year - 90, year - 1),
    sex: pick(rng, ["M", "F", "F", "M", "<"]),
    expiryDate: randomDate(rng, year - 3, year + 10),
    personalNumber: rng() < 0.5 ? "" : randomString(rng, int(rng, 1, 14)),
    optionalData1: rng() < 0.5 ? "" : randomString(rng, int(rng, 1, LAYOUTS[format].format === "TD1" ? 15 : 7)),
    optionalData2: rng() < 0.7 ? "" : randomString(rng, int(rng, 1, 11))
  };
  return { ...doc, ...options.overrides, format };
}
var mrzText = (s) => s.toUpperCase().replace(/[^A-Z0-9]+/g, "<");
var yymmdd = (iso) => iso.slice(2, 4) + iso.slice(5, 7) + iso.slice(8, 10);
var fit = (s, n) => s.slice(0, n).padEnd(n, "<");
var cd = (s) => String(computeCheckDigit(s) ?? 0);
function encodeName(surname, givenNames, length) {
  const name = givenNames ? `${mrzText(surname)}<<${mrzText(givenNames)}` : mrzText(surname);
  return fit(name, length);
}
function buildMrz(format, doc) {
  const code = fit(doc.documentCode, 2);
  const state = fit(doc.issuingState, 3);
  const nat = fit(doc.nationality, 3);
  const dob = yymmdd(doc.birthDate);
  const exp = yymmdd(doc.expiryDate);
  const num = mrzText(doc.documentNumber);
  const sex = doc.sex;
  const docField = (optLength2, opt2) => {
    if (num.length <= 9 || format === "TD3" || format === "MRVA" || format === "MRVB") {
      const n = fit(num, 9);
      return [n + cd(n), fit(mrzText(opt2), optLength2)];
    }
    const overflow = num.slice(9) + cd(num) + "<";
    return [num.slice(0, 9) + "<", fit(overflow + mrzText(opt2), optLength2)];
  };
  if (format === "TD1") {
    const [docPart2, opt1] = docField(15, doc.optionalData1);
    const l12 = code + state + docPart2 + opt1;
    const l2a = dob + cd(dob) + sex + exp + cd(exp) + nat + fit(mrzText(doc.optionalData2), 11);
    const composite = cd(l12.slice(5, 30) + l2a.slice(0, 7) + l2a.slice(8, 15) + l2a.slice(18, 29));
    return [l12, l2a + composite, encodeName(doc.surname, doc.givenNames, 30)];
  }
  const long = format === "TD3" || format === "MRVA";
  const l1 = code + state + encodeName(doc.surname, doc.givenNames, long ? 39 : 31);
  const optLength = format === "TD3" ? 14 : format === "TD2" ? 7 : format === "MRVA" ? 16 : 8;
  const [docPart, opt] = docField(
    optLength,
    format === "TD3" ? doc.personalNumber : doc.optionalData1
  );
  let l2 = docPart + nat + dob + cd(dob) + sex + exp + cd(exp) + opt;
  if (format === "TD3") {
    l2 += /^<+$/.test(opt) ? "<" : cd(opt);
    l2 += cd(l2.slice(0, 10) + l2.slice(13, 20) + l2.slice(21, 43));
  } else if (format === "TD2") {
    l2 += cd(l2.slice(0, 10) + l2.slice(13, 20) + l2.slice(21, 35));
  }
  return [l1, l2];
}
function generateMrz(options = {}) {
  const { format, ...document } = randomDocument(options);
  const lines = buildMrz(format, document);
  return { format, lines, text: lines.join("\n"), document };
}
function mutateOcr(lines, options = {}) {
  const rng = options.rng ?? createRng(options.seed ?? 1);
  const grid = lines.map((l) => [...l]);
  const confusable = (c) => {
    const alts = [...LETTERS_FOR_DIGIT[c]?.slice(0, 1) ?? []];
    for (const [letter, digit] of Object.entries(DIGIT_FOR_LETTER)) {
      if (letter === c && "OIZSGB".includes(letter)) alts.push(digit);
    }
    if (c === "<" && options.fillerErrors) alts.push("K");
    return alts;
  };
  const all = options.positions ?? grid.flatMap((row, l) => row.map((_, i) => [l, i]));
  const candidates = all.filter(([l, i]) => confusable(grid[l]?.[i] ?? "").length > 0);
  const mutations = [];
  for (let n = 0; n < (options.count ?? 1) && candidates.length; n++) {
    const idx = Math.floor(rng() * candidates.length);
    const [l, i] = candidates.splice(idx, 1)[0];
    const from = grid[l][i];
    const to = pick(rng, confusable(from));
    grid[l][i] = to;
    mutations.push({ line: l, position: i, from, to });
  }
  return { lines: grid.map((r) => r.join("")), mutations };
}
export {
  buildMrz,
  createRng,
  encodeName,
  generateMrz,
  inventName,
  mutateOcr,
  randomDocument
};
//# sourceMappingURL=generate.js.map